import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { useUi, type IUiLinkProps } from '../../provider/UiContext';
import './Header.scss';

export interface IHeaderProps {
    /** Бренд слева — обычно `HeaderBrand` (логотип-ссылка на главную) */
    brand?: ReactNode;
    /** Метки рядом с брендом: стенд, режим доступа */
    brandAside?: ReactNode;
    /**
     * Поле поиска — первым в правой группе. Слот раскрывается на всю ширину, пока поле `.rf-search__input` в фокусе
     * или заполнено и пока в нём есть `[data-search-expanded='true']` (открыта панель поиска).
     */
    search?: ReactNode;
    /** Кнопки шапки перед меню пользователя (`Button variant="header"`) */
    actions?: ReactNode;
    /** Меню пользователя: `UserMenuDialog` с якорем `UserMenuButton` */
    userMenu?: ReactNode;
    /** Прочее содержимое: окна и плавающие панели шапки */
    children?: ReactNode;
    className?: string;
}

export interface IHeaderBrandProps extends Omit<IUiLinkProps, 'className' | 'children'> {
    /** Логотип (и метка рядом с ним) */
    children: ReactNode;
}

/** Логотип-ссылка шапки: переход — ссылкой приложения (`UiProvider.linkComponent`). */
export const HeaderBrand: React.FC<IHeaderBrandProps> = ({ children, ...linkProps }) => {
    const { linkComponent: Link } = useUi();

    return (
        <Link {...linkProps} className="rf-navbar__logo-link">
            <div className="logo-button">{children}</div>
        </Link>
    );
};

/**
 * Шапка приложения: бренд, метки, поиск, кнопки и меню пользователя на градиенте оболочки. Ставится слотом `header`
 * у `AppShell` — бургер бокового меню перед ней рисует оболочка. Классы — прежние классы шапки Front (`rf-navbar`,
 * `controls`, `search`, `items`, `user`): на них опираются вход оболочки и раскладка шапки дашборда на телефоне.
 */
const Header: React.FC<IHeaderProps> = ({ brand, brandAside, search, actions, userMenu, children, className }) => (
    <div className={clsx('rf-navbar', className)}>
        {brand}
        {brandAside && <div className="rf-navbar__marks">{brandAside}</div>}
        <div className="controls">
            {search && <div className="search">{search}</div>}
            {actions && <div className="items">{actions}</div>}
            {userMenu && <div className="user">{userMenu}</div>}
        </div>
        {children}
    </div>
);

export default Header;
